// pages/Write.jsx
import "../../utils/quillInit";
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Context } from "../../context/Context";
import axios from "axios";
import { toast } from "react-toastify";
import "./write.css";
import { createSlug, decodeHtml } from "../../utils/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faPenToSquare,
  faTrashCan,
  faUnlock,
} from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import SeoAnalyser from "./SeoAnalyser";
import ReadabilityAnalyser from "./ReadabilityAnalyser";
import FeatureImageUploadModal from "../../components/modals/FeatureImageUploadModal";
import ImageUploadModal from "../../components/modals/ImageUploadModal";
import Quill from "quill";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ConfirmModal from "../../components/confirmModal/ConfirmModal";
import { FaCalendarAlt } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import { detectPassiveVoice, detectLongSentences } from "../../utils/utils";
import SidebarDraftManager from "../../components/write/SidebarDraftManager";
import MetaData from "../../components/write/MetaData";

const Delta = Quill.import("delta");

const LoadingComponent = () => (
  <div style={{ textAlign: "center", paddingTop: "100px", fontSize: "18px" }}>
    Loading writer dashboard...
  </div>
);

const UnauthorizedComponent = () => (
  <div style={{ textAlign: "center", paddingTop: "100px", fontSize: "18px" }}>
    Not Authorized!
  </div>
);

export default function Write() {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [url, setUrl] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [postSlug, setPostSlug] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [focusKeyphrase, setFocusKeyphrase] = useState("");
  const { user, token } = useContext(Context);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [editors, setEditors] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [articles, setArticles] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentArticleId, setCurrentArticleId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const [visibleTooltip, setVisibleTooltip] = useState(null);
  const [mainAuthor, setMainAuthor] = useState("");
  const [editor, setEditor] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const quillRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "draft" or "article"
  const [scheduledDate, setScheduledDate] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [desc, setDesc] = useState("");
  const [status, setStatus] = useState("draft");
  const [publishedAt, setPublishedAt] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);

  const location = useLocation();
  const draftSlug = new URLSearchParams(location.search).get("draft");

  const [showImageModal, setShowImageModal] = useState(false);
  const [editingImageData, setEditingImageData] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);

  const [editorHtml, setEditorHtml] = useState("");

  const [hasInlineFeedback, setHasInlineFeedback] = useState(false);

  const [showFeatureImageModal, setShowFeatureImageModal] = useState(false);
  const [featuredImageData, setFeaturedImageData] = useState(null); // { src, variants, filenameBase, alt, attribution, caption }

  useEffect(() => {
    const timeout = setTimeout(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const updateHtml = () => {
        const html = quill.root.innerHTML;
        const text = quill.getText().trim();
        const count = text.length > 0 ? text.split(/\s+/).length : 0;

        setEditorHtml(html);
        setWordCount(count);
      };

      updateHtml();

      quill.on("text-change", updateHtml);

      return () => {
        quill.off("text-change", updateHtml);
      };
    }, 0);

    return () => clearTimeout(timeout);
  }, []);

  const getFeaturedImagePayload = () => {
    if (!featuredImageData?.src) return null;

    return {
      photo: featuredImageData.src,
      photoAlt: featuredImageData.alt || "",
      photoAttribution: featuredImageData.attribution || "",
      photoCaption: featuredImageData.caption || "",
      photoVariants: featuredImageData.variants || [],
      filenameBase: featuredImageData.filenameBase || "",
    };
  };

  const loadArticles = async () => {
    try {
      const url =
        user.role >= 4
          ? "/posts?all=true" // Admins get all posts
          : "/posts";          // Others get filtered set

      const articlesRes = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const allArticles = articlesRes.data;

      // Published articles: filter + sort by publishedAt (desc)
      const publishedArticles = allArticles
        .filter((article) => article.status === "published" && article.publishedAt)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

      // Drafts (visible based on role)
      const visibleDrafts = allArticles.filter((article) => {
        if (article.status !== "draft") return false;
        if (user.role >= 4) return true; // Admins see all drafts
        if (user.role === 3) return article.editorId === user.id;
        if (user.role === 2) return article.authorId === user.id;
        return false; // Readers and unauthorized users
      });

      setArticles(publishedArticles);
      setDrafts(visibleDrafts);

    } catch (err) {
      console.error("Error fetching articles", err);
    }
  };

  const handleEditDraft = (draftSlug) => {
    //setIsSidebarOpen(!isSidebarOpen);
    toggleSidebar();
    navigate(`/write?draft=${draftSlug}`);
  };

  const handleEditArticle = (articleSlug) => {
    //setIsSidebarOpen(!isSidebarOpen);
    toggleSidebar();
    navigate(`/write?draft=${articleSlug}`);
  };

  const handleDeleteArticle = async (articleId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to delete articles.");
      return;
    }

    try {
      await axios.delete(`posts/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setArticles((prev) => prev.filter((article) => article._id !== articleId));

      toast.success("Article deleted successfully!");
    } catch (err) {
      console.error("Error deleting article", err);
      toast.error("Error deleting article");
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to delete drafts.");
      return;
    }

    try {
      await axios.delete(`posts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDrafts((prev) => prev.filter((draft) => draft._id !== draftId));

      if (currentArticleId === draftId) {
        setCurrentArticleId(null);
        navigate("/write");
      }

      toast.success("Draft deleted successfully!");
    } catch (err) {
      console.error("Error deleting draft", err);
      toast.error("Error deleting draft");
    }
  };

  const handleSwitchToPublish = async (draftId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to publish.");
      return;
    }

    try {
      const newArticle = {
        status: "published",
        scheduledAt: null,
      };

      await axios.put(`/posts/${draftId}`, newArticle, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDrafts(drafts.filter((draft) => draft._id !== draftId));
      toast.success("Successfully switched to article!");
      loadArticles();
    } catch (err) {
      console.error("Error switching to article:", err);
      toast.error("Failed to switch to article.");
    }
  };

  const handleSwitchToDraft = async (articleId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to unpublish.");
      return;
    }

    try {
      const newArticle = {
        status: "draft",
        scheduledAt: null,
      };

      await axios.put(`/posts/${articleId}`, newArticle, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setArticles(articles.filter((art) => art._id !== articleId));
      toast.success("Successfully switched to draft!");
      loadArticles();
    } catch (err) {
      console.error("Error switching to draft:", err);
      toast.error("Failed to switch to draft.");
    }
  };

  const getMainAuthor = async () => {
    const res = await axios.get(`/users`);
    const authors = res.data.filter((user) => user.isAuthor);
    const author = authors.find(
      (author) => author.user_name === mainAuthor.label
    );
    let authorId;
    if (author) {
      authorId = author.userId;
    }
    return authorId;
  };

  const getCategories = async () => {
    try {
      const res = await axios.get(`/categories`);
      const categories = res.data.filter((cat) =>
        selectedCategories
          .map((category) => category.value)
          .includes(cat.cat_slug)
      );
      return categories;
    } catch (err) {
      console.error("Error fetching categories!", err);
    }
  };

  const getTags = async () => {
    try {
      const res = await axios.get(`/tags`);
      const tags = res.data.filter((tag) =>
        selectedTags.map((tag) => tag.value).includes(tag.tag_slug)
      );
      return tags;
    } catch (err) {
      console.error("Error fetching tags!", err);
    }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate("/login");
    }

    try {
      const userRole = Number(jwtDecode(token)?.role);
      setIsAuthorized(userRole > 2);
    } catch (err) {
      console.error("Invalid token", err);
      setIsAuthorized(false);
    }
  }, [user, token, navigate]);

  const loadCategoriesAndTags = async () => {
    try {
      const usersRes = await axios.get(`/users`);
      const allUsers = usersRes.data;

      const authors = allUsers.filter((user) => user.isAuthor);
      const editors = allUsers.filter((user) => user.role === 3);

      setAuthors(
        authors.map((user) => ({
          label: decodeHtml(user.user_name),
          value: user.userId,
        }))
      );

      setEditors(
        editors.map((user) => ({
          label: decodeHtml(user.user_name),
          value: user._id,
        }))
      );

      const categoriesRes = await axios.get(`/categories`);
      setCategories(
        categoriesRes.data.map((cat) => ({
          label: decodeHtml(cat.cat_name),
          value: cat.cat_slug,
        }))
      );

      const tagsRes = await axios.get(`/tags`);
      setTags(
        tagsRes.data.map((tag) => ({
          label: decodeHtml(tag.tag_name),
          value: tag.tag_slug,
        }))
      );

    } catch (err) {
      console.error("Error fetching categories or tags", err);
    }
  };

  const loadDraft = async (slug) => {
    try {
      const draftRes = await axios.get(`/posts/${slug}`);
      const draft = draftRes.data;

      setTitle(draft.title);
      setPostSlug(draft.post_slug);
      setSeoTitle(draft.seoTitle || "");
      setMetaDescription(draft.metaDescription || "");
      setFocusKeyphrase(draft.focusKeyphrase || "");
      setUrl(draft.url || "");
      setCaption(draft.photoCaption || "");
      setSelectedCategories(
        draft.categories.map((cat) => ({
          label: decodeHtml(cat.cat_name),
          value: decodeHtml(cat.cat_slug),
        }))
      );
      setSelectedAuthors(draft.authors);
      setSelectedTags(
        draft.tags.map((tag) => ({
          label: decodeHtml(tag.tag_name),
          value: decodeHtml(tag.tag_slug),
        }))
      );
      setCurrentArticleId(draft._id);
      setIsUpdating(true);

      const author = await axios.get(`/users/${draft.authorId}`);
      setMainAuthor({
        label: author.data.user_name,
        value: author.data.user_slug,
      });

      const editor = await axios.get(`/users/${draft.editorId}`);
      setEditor({
        label: editor.data.user_name,
        value: editor.data.user_slug,
      });

      // Set the featured image (new format)
      if (draft.featuredImage) {
        setFeaturedImageData({
          src: draft.featuredImage.src,
          variants: draft.featuredImage.variants || [],
          filenameBase: draft.featuredImage.filenameBase || "",
          alt: draft.featuredImage.alt || "",
          attribution: draft.featuredImage.attribution || "",
          caption: draft.featuredImage.caption || "",
        });
      }

      setTimeout(() => {
        const quill = quillRef.current?.getEditor();
        if (quill && draft.desc) {
          quill.clipboard.dangerouslyPasteHTML(draft.desc);
          setEditorHtml(draft.desc || "");
        }
      }, 0);

      setPublishedAt(draft.publishedAt || null);
      setScheduledAt(draft.scheduledAt || null);
      setStatus(draft.status);

    } catch (err) {
      console.error("Error fetching draft", err);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await loadCategoriesAndTags();
      await loadArticles();
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadDraftIfReady = async () => {
      if (!draftSlug || authors.length === 0 || editors.length === 0) return;

      try {
        const res = await axios.get(`/posts/slug/${draftSlug}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const article = res.data;

        setMainAuthor(
          authors.find((a) => Number(a.value) === Number(article.authorId)) || null
        );
        setEditor(
          editors.find((e) => String(e.value) === String(article.editorId)) || null
        );
        setTitle(article.title);
        setSeoTitle(article.seoTitle);
        setMetaDescription(article.metaDescription);
        setFocusKeyphrase(article.focusKeyphrase);
        setPostSlug(article.post_slug);
        setSelectedCategories(
          article.categories?.map((category) =>
            categories.find((c) => c.value === category.cat_slug)
          ).filter(Boolean) || []
        );
        setSelectedTags(
          article.tags?.map((tag) =>
            tags.find((t) => t.value === tag.tag_slug)
          ).filter(Boolean) || []
        );
        setCaption(article.photoCaption || "");
        setDesc(article.desc || "");
        quillRef.current?.getEditor().clipboard.dangerouslyPasteHTML(
          article.desc || ""
        );
        setEditorHtml(article.desc || "");

        setCurrentArticleId(article._id);
        setIsUpdating(true);
        setStatus(article.status);
        setPublishedAt(article.publishedAt || null);
        setScheduledAt(article.scheduledAt || null);
      } catch (err) {
        console.error("Error loading draft:", err);
      }
    };

    loadDraftIfReady();
  }, [draftSlug, authors, editors, categories, tags]);

  useEffect(() => {
    const handleClick = (e) => {
      const editorRoot = quillRef.current?.getEditor()?.root;
      if (!editorRoot || !editorRoot.contains(e.target)) return;

      const blotNode = e.target.closest(".ql-customImage");
      if (!blotNode) return;

      const img = blotNode.querySelector("img");
      if (!img) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const index = editor.getIndex(Quill.find(blotNode));
      setEditingImageData({
        src: img.getAttribute("src"),
        alt: img.getAttribute("alt"),
        attribution: img.getAttribute("title") || "",
        caption: blotNode.querySelector(".ql-customImage-caption")?.textContent || "",
        variants: blotNode.dataset.variants ? JSON.parse(blotNode.dataset.variants) : [],
        filenameBase: blotNode.dataset.filenameBase || "",
      });
      setEditingImageIndex(index);
      setShowImageModal(true);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const editor = quill.root;

    // Disable drag-and-drop image inserts
    const handleDrop = (e) => {
      const hasImage = Array.from(e.dataTransfer?.items || []).some((item) =>
        item.type.startsWith("image/")
      );
      if (hasImage) {
        e.preventDefault();
        toast.warning("Dragging and dropping images is disabled. Please use the upload button.");
      }
    };

    editor.addEventListener("drop", handleDrop);

   return () => {
      editor.removeEventListener("drop", handleDrop);
    };
  }, []);

  const IMAGE_PLACEHOLDER_TEXT = "[Image Removed – Please use the upload button.]";
  // Used in sanitizeImages() and containsSanitizedImages() to detect unauthorized images

  const sanitizeImages = (html) => {
    const allowedPrefix = process.env.REACT_APP_AWS_S3_IMAGE_URL?.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const pattern = new RegExp(
      `<img[^>]+src=["']((?!${allowedPrefix}).*?)["'][^>]*>`,
      "g"
    );
    return html.replace(
      pattern,
      `<span class="image-placeholder">${IMAGE_PLACEHOLDER_TEXT}</span>`
    );
  };

  const containsSanitizedImages = (html) => {
    const div = document.createElement("div");
    div.innerHTML = sanitizeImages(html);
    return !!div.querySelector(".image-placeholder");
  };

  const validateFields = () => {
    const content = quillRef.current?.getEditor()?.root?.innerHTML || "";

    if (
      !title ||
      !content ||
      !seoTitle ||
      !metaDescription ||
      !focusKeyphrase ||
      selectedCategories.length === 0 ||
      !mainAuthor ||
      !editor
    ) {
      toast.error("Please fill in all required fields marked with *");
      return false;
    }

    if (containsSanitizedImages(content)) {
      toast.error("One or more unauthorized images were removed. Please use the image upload button to add them properly.");
      return false;
    }

    return true;
  };

  const extractText = (html) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;
    return tempElement.textContent || tempElement.innerText || "";
  };

  const handleSaveAsDraft = async () => {
    if (!validateFields()) return;

    setIsLoading(true);

    const authorId = await getMainAuthor();
    const editorId = editor?.value;
    const categories = await getCategories();
    const tags = await getTags();
    const quillHtml = quillRef.current?.getEditor()?.root?.innerHTML || "";
    const plainDesc = extractText(quillHtml);

    const newArticle = {
      authorId,
      authors: selectedAuthors,
      editorId,
      title,
      shortDesc:
        plainDesc.slice(0, 150) + (plainDesc.length > 150 ? "..." : ""),
      desc: sanitizeImages(quillHtml),
      categories,
      tags,
      seoTitle,
      metaDescription,
      focusKeyphrase,
      url,
      status: "draft",
      published: false,
      post_slug: postSlug,
      scheduledAt: status === "draft" && scheduledDate ? new Date(scheduledDate) : null,
    };

    // Add featured image metadata if available
    const featuredImagePayload = getFeaturedImagePayload();
    if (featuredImagePayload) {
      Object.assign(newArticle, featuredImagePayload);
    }

    try {
      if (isUpdating) {
        await axios.put(`/posts/${currentArticleId}`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Draft updated successfully!");
      } else {

        const res = await axios.post(`/posts`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsUpdating(true);
        setCurrentArticleId(res.data._id);
        toast.success("Draft saved successfully!");
      }

      loadArticles();
    } catch (err) {
      console.error("Error saving draft", err);
      toast.error("Error saving draft");
    } finally {
      setIsLoading(false);
    }
  };

  const isScheduled = status === "draft" && scheduledDate && new Date(scheduledDate) > new Date();
  const buttonLabel = isScheduled ? "Schedule" : "Publish Now";

  const handlePublish = async () => {
    if (user.role < 4) {
      toast.error("You are not authorized to publish.");
      return;
    }

    if (!validateFields()) return;

    if (featuredImageData?.src) {
      if (!featuredImageData.alt?.trim() || !featuredImageData.attribution?.trim()) {
        toast.error("Featured image must include alt text and attribution.");
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);

    const authorId = await getMainAuthor();
    const editorId = editor?.value;
    const categories = await getCategories();
    const tags = await getTags();
    const quillHtml = quillRef.current?.getEditor()?.root?.innerHTML || "";
    const plainDesc = extractText(quillHtml);
    const newArticle = {
      authorId,
      authors: selectedAuthors,
      editorId,
      title,
      shortDesc:
        plainDesc.slice(0, 150) + (plainDesc.length > 150 ? "..." : ""),
      desc: sanitizeImages(quillHtml),
      categories,
      tags,
      seoTitle,
      metaDescription,
      focusKeyphrase,
      url,
      status: isScheduled ? "draft" : "published",
      published: !isScheduled,
      post_slug: postSlug,
      scheduledAt: isScheduled ? new Date(scheduledDate) : null,
    };

    const featuredImagePayload = getFeaturedImagePayload();
    if (featuredImagePayload) {
      Object.assign(newArticle, featuredImagePayload);
    }

    const tokenPreview = token?.slice(0, 10) + "...";
    console.log("Token:", tokenPreview);
    console.log("Payload:", newArticle);
    console.log("currentArticleId:", currentArticleId);
    console.log("Authorization header:", `Bearer ${tokenPreview}`);
    console.log("Selected categories:", selectedCategories);
    console.log("Selected editor:", editor);

    try {
      if (isUpdating) {
        console.log("Updating article with ID:", currentArticleId);
        await axios.put(`/posts/${currentArticleId}`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Article updated successfully!");
      } else {
        console.log("Creating new article");
        await axios.post(`/posts`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (isScheduled) {
          toast.success("Article scheduled successfully!");
        } else {
          toast.success("Article published successfully!");
        }
      }

      loadArticles();
    } catch (err) {
      if (err.response) {
        console.error("Error publishing article (response):", err.response.data);
        console.error("Status:", err.response.status);
      } else if (err.request) {
        console.error("Error publishing article (request):", err.request);
      } else {
        console.error("Error publishing article (message):", err.message);
      }
      toast.error(err?.response?.data?.message || "Error publishing article");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDraft = () => {
    setTitle("");
    setPostSlug("");
    setSeoTitle("");
    setMetaDescription("");
    setFocusKeyphrase("");
    setCaption("");
    setUrl("");
    setSelectedCategories([]);
    setMainAuthor("");
    setEditor("");
    setSelectedAuthors([]);
    setSelectedTags([]);
    setCurrentArticleId("");
    setFeaturedImageData(null)
    setIsUpdating(false);
    setStatus("draft");
    setPublishedAt(null);
    navigate("/write");

    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.setContents([]); // clear content
    }
    setEditorHtml(""); // also reset analysis source
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    document.body.style.overflow = isSidebarOpen ? "auto" : "hidden"; // Toggle scrolling
  };

  const toggleTooltip = (id) => {
    setVisibleTooltip((prev) => (prev === id ? null : id));
  };

  const addTagToDatabase = async (newTag) => {
    try {
      const response = await axios.post(
        "/tags",
        { tag_name: newTag, tag_slug: createSlug(newTag) },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 201) {
        const createdTag = response.data;
        setTags((prevTags) => [...prevTags, createdTag]);
      }
    } catch (error) {
      console.error("Error adding tag to the database:", error);
    }
  };

  const handleTagChange = (opts) => {
    const newTags = opts.filter(
      (opt) => !tags.some((tag) => tag.value === opt.value)
    );
    newTags.forEach((newTag) => addTagToDatabase(newTag.label));

    setSelectedTags(
      opts.map((opt) => ({
        label: opt.label,
        value: decodeHtml(opt.value),
      }))
    );
  };

  const handleContentImages = () => {
    setEditingImageData(null);
    setEditingImageIndex(null);
    setShowImageModal(false); // Ensure modal unmounts
    setTimeout(() => {
      setShowImageModal(true);
    }, 0); // Trigger fresh mount with cleared state
  };

  const handleInsertImage = (imageData) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const index = editingImageIndex ?? quill.getSelection(true)?.index ?? 0;

    // Remove old image if editing
    if (editingImageIndex !== null) {
      quill.deleteText(editingImageIndex, 1, "user");
    }

    quill.insertEmbed(index, "customImage", {
      src: imageData.src,
      alt: imageData.alt,
      attribution: imageData.attribution,
      caption: imageData.caption,
      variants: imageData.variants,
      filenameBase: imageData.filenameBase,
    });

    quill.setSelection(index + 1, 0, "user");

    setEditingImageData(null);
    setEditingImageIndex(null);
    setShowImageModal(false);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [
            { header: "1" },
            { header: "2" },
            {
              font: [],
            },
          ],
          [{ size: [] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: {
          image: handleContentImages,
        },
      },
      clipboard: {
        matchers: [
          ['img', (node, delta) => {
            if (node.src?.startsWith('data:image/')) {
              toast.warning("Pasted images are not allowed. Please use the upload button.");
              return new Delta(); // Discard pasted base64 images
            }
            return delta; // Allow images from trusted sources (e.g. S3 URLs from upload modal)
          }],
        ],
      }
    }),
    []
  );

  const handlePaste = (e) => {
    // Block pasted image *files* (e.g., screenshots, dragged images)
    if (e.clipboardData?.files?.length) {
      const containsImage = Array.from(e.clipboardData.files).some((file) =>
        file.type.startsWith("image/")
      );
      if (containsImage) {
        e.preventDefault();
        toast.warning("Pasting images is disabled. Please use the upload button.");
        return;
      }
    }

    // Block pasted image *HTML* (e.g., <img src="...">)
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("text/html");

    if (/<img[^>]+src=["'](data:image\/|https?:\/\/)/.test(pastedData)) {
      e.preventDefault();
      toast.warning("Pasting images is disabled. Please use the upload button.");
    }
  };

  if (!isAuthorized) {
    return <UnauthorizedComponent />;
  }

  const unscheduledDrafts = drafts.filter(
    (draft) => draft.status === "draft" && (!draft.scheduledAt)
  );

  const scheduledDrafts = drafts.filter(
    (draft) => draft.status === "draft" && draft.scheduledAt
  );

  const visibleDrafts = unscheduledDrafts.filter((d) => {
    if (user.role >= 4) return true;
    if (user.role === 3) return d.editorId === user.id;
    if (user.role === 2) return d.authorId === user.id;
    return false;
  });

  const runInlineFeedback = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const text = quill.getText();
    const length = quill.getLength();

    // Clear old formatting
    quill.formatText(0, length, "passive-voice", false);
    quill.formatText(0, length, "long-sentence", false);

    // Detect issues
    const passiveMatches = detectPassiveVoice(text);
    const longSentenceMatches = detectLongSentences(text);

    // Apply passive voice formatting first
    passiveMatches.forEach(({ start, end, message }) => {
      quill.formatText(start, end - start, "passive-voice", message);
    });

    // Apply long sentence formatting only if no passive already exists
    longSentenceMatches.forEach(({ start, end, message }) => {

      // Build subranges that exclude passive spans
      let ranges = [{ start, end }];

      passiveMatches.forEach(({ start: pStart, end: pEnd }) => {
        ranges = ranges.flatMap(({ start: rStart, end: rEnd }) => {
          if (pEnd <= rStart || pStart >= rEnd) {
            // No overlap
            return [{ start: rStart, end: rEnd }];
          }

          const segments = [];
          if (pStart > rStart) {
            segments.push({ start: rStart, end: pStart });
          }
          if (pEnd < rEnd) {
            segments.push({ start: pEnd, end: rEnd });
          }
          return segments;
        });
      });

      // Apply long-sentence formatting to non-overlapping parts
      ranges.forEach(({ start, end }) => {
        if (end > start) {
          quill.formatText(start, end - start, "long-sentence", message);
        }
      });
    });

    setHasInlineFeedback(true);
  };

  return (
   <>
    <FeatureImageUploadModal
      isOpen={showFeatureImageModal}
      onClose={() => setShowFeatureImageModal(false)}
      onConfirm={(data) => {
        setFeaturedImageData(data);
        setShowFeatureImageModal(false);
      }}
      initialData={featuredImageData}
      isEditMode={!!featuredImageData}
    />

    <ImageUploadModal
      isOpen={showImageModal}
      onClose={() => {
        setShowImageModal(false);
        setEditingImageData({});
        setEditingImageIndex(null);
      }}
      onInsert={handleInsertImage}
      initialData={editingImageData}
      isEditMode={!!editingImageData}
    />

    <div className="Write">
      <SidebarDraftManager
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        visibleDrafts={visibleDrafts}
        scheduledDrafts={scheduledDrafts}
        articles={articles}
        user={user}
        handleEditDraft={handleEditDraft}
        handleEditArticle={handleEditArticle}
        handleSwitchToPublish={handleSwitchToPublish}
        handleSwitchToDraft={handleSwitchToDraft}
        setDeleteType={setDeleteType}
        setConfirmDeleteId={setConfirmDeleteId}
      />

      <div className="write">
        <div
          className="write-title"
          style={{ position: "relative", display: "flex", alignItems: "center", gap: "40px" }}
        >
          <button
            style={{
              width: "127px",
              height: "32px",
              borderRadius: "10px",
              backgroundColor: "#272635",
              color: "#B1E5F2",
              border: "none",
              cursor: "pointer",
              fontSize: "15px",
            }}
            onClick={handleCancelDraft}
          >
            + NEW ARTICLE
          </button>
          <p
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              margin: 0,
            }}
          >
            The Writer's Room
          </p>
        </div>
        <form className="writeForm" onSubmit={(e) => e.preventDefault()}>
          <MetaData
            mainAuthor={mainAuthor}
            setMainAuthor={setMainAuthor}
            selectedAuthors={selectedAuthors}
            setSelectedAuthors={setSelectedAuthors}
            authors={authors}
            editor={editor}
            setEditor={setEditor}
            editors={editors}
            title={title}
            setTitle={setTitle}
            seoTitle={seoTitle}
            setSeoTitle={setSeoTitle}
            metaDescription={metaDescription}
            setMetaDescription={setMetaDescription}
            focusKeyphrase={focusKeyphrase}
            setFocusKeyphrase={setFocusKeyphrase}
            postSlug={postSlug}
            setPostSlug={setPostSlug}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            categories={categories}
            tags={tags}
            selectedTags={selectedTags}
            handleTagChange={handleTagChange}
            featuredImageData={featuredImageData}
            setFeaturedImageData={setFeaturedImageData}
            setShowFeatureImageModal={setShowFeatureImageModal}
            toggleTooltip={toggleTooltip}
            visibleTooltip={visibleTooltip}
          />
          <div className="writeFormGroup">
            <label>Content:</label>
            <ReactQuill
              ref={quillRef}
              style={{ border: "none" }}
              onPaste={handlePaste}
              modules={modules}
              formats={[
                "header",
                "font",
                "size",
                "bold",
                "italic",
                "underline",
                "strike",
                "blockquote",
                "list",
                "bullet",
                "indent",
                "link",
                "image",
                "video",
                "customImage",
                "passive-voice",
                "long-sentence",
              ]}
              className="writeText"
            />
            <div className="tooltip-container">
              <i className="info-icon" onClick={() => toggleTooltip("content")}>ℹ️</i>
              {visibleTooltip === "content" && <div className="tooltip">Minimum 500 words</div>}
            </div>
          </div>
          <div className="writeFormGroup">
            <label style={{ visibility: "hidden", fontSize: "0px" }}>Label</label>
            <div style={{fontSize: "12px", fontWeight: "bold" }}>
              Word Count: {wordCount}
            </div>
            {!hasInlineFeedback ? (
              <button
                className="runInlineFeedbackButton"
                onClick={runInlineFeedback}
              >
                Run Inline Feedback
              </button>
            ) : (
              <button
                className="clearInlineFeedbackButton"
                onClick={() => {
                  const quill = quillRef.current?.getEditor();
                  if (!quill) return;
                  const length = quill.getLength();
                  ["passive-voice", "long-sentence"].forEach((blot) => {
                    quill.formatText(0, length, blot, false);
                  });

                  setHasInlineFeedback(false);
                }}
              >
                Clear Inline Feedback
              </button>
            )}
          </div>
          <div className="writeFormGroup-buttons">
            {user?.role >= 4 && (
              <>
                <FontAwesomeIcon
                  icon={faTrashCan}
                  style={{
                    marginRight: "15px",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                  title="Delete Draft"
                  onClick={() => {
                    setDeleteType("draft");
                    setConfirmDeleteId(currentArticleId);
                  }}
                />
                {status !== "published" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span title="Schedule for future publication">
                      <FaCalendarAlt
                        title="Schedule for future publication"
                        onClick={() => {
                          const dateInput = document.getElementById("schedule-input");
                          if (dateInput) dateInput.showPicker();
                        }}
                        style={{
                          fontSize: "20px",
                          cursor: "pointer",
                          color: scheduledDate ? "green" : "#333",
                        }}
                      />
                    </span>
                    <input
                      id="schedule-input"
                      type="datetime-local"
                      value={scheduledDate ? new Date(scheduledDate).toISOString().slice(0, 16) : ""}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      style={{
                        display: "none",
                        opacity: 0,
                        left: 0,
                        width: "32px",
                        height: "32px",
                        cursor: "pointer",
                      }}
                    />
                    {scheduledDate && (
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        Scheduled for: {new Date(scheduledDate).toLocaleString()}
                      </span>
                    )}
                    {scheduledDate && (
                      <FaTimes
                        onClick={() => setScheduledDate(null)}
                        style={{
                          color: "#888",
                          fontSize: "14px",
                          cursor: "pointer",
                          transition: "color 0.2s",
                        }}
                        title="Clear scheduled date"
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#d00")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#888")}
                      />
                    )}
                  </div>
                ) : publishedAt ? (
                  <div style={{ fontSize: "14px", color: "#555" }}>
                    Published on {new Date(publishedAt).toLocaleString()}
                  </div>
                ) : null}
                <button className="writeSubmit" onClick={handlePublish} disabled={isLoading}>
                  {isLoading ? "Processing..." : buttonLabel}
                </button>
              </>
            )}
            <button className="writeSubmitDraft" onClick={handleSaveAsDraft} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save as Draft"}
            </button>
          </div>
        </form>

        {confirmDeleteId && (
          <ConfirmModal
            message={`Are you sure you want to permanently delete this ${deleteType}?`}
            onConfirm={() => {
              if (deleteType === "draft") {
                handleDeleteDraft(confirmDeleteId);
              } else {
                handleDeleteArticle(confirmDeleteId);
              }
              setConfirmDeleteId(null);
              setDeleteType("");
            }}
            onCancel={() => {
              setConfirmDeleteId(null);
              setDeleteType("");
            }}
          />
        )}

        {editorHtml && (
          <>
            <div className="analyser-card">
              <SeoAnalyser
                seoTitle={seoTitle}
                metaDescription={metaDescription}
                focusKeyphrase={focusKeyphrase}
                content={editorHtml}
                postSlug={postSlug}
                caption={caption}
                previousArticles={articles}
                featuredImageUrl={featuredImageData?.src}
              />
            </div>
            <div className="analyser-card">
              <ReadabilityAnalyser
                content={editorHtml}
              />
            </div>
          </>
        )}
      </div>
    </div>
   </>
  );
}
